/*
 * TierPill — small "Free · Upgrade" badge surfaced in:
 *   - MyBusiness hero (Phase 4.5)
 *   - Anywhere else a quick "what tier am I on" hint is useful
 *
 * Each tier has a distinct color tone matching the brand kit:
 *   free      → neutral gray
 *   workspace → blue (brand)
 *   solo   → blue (brand)
 *   team   → yellow (Pulse / accent)
 *   scale  → indigo (deeper authority)
 *   custom → magenta-ish (enterprise editorial accent)
 *
 * Click navigates to /client/billing.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';


export type TierSlug =
  | 'free' | 'workspace' | 'solo' | 'team' | 'scale' | 'custom'
  // Legacy chatbot tiers — render as a generic "Legacy" pill so the
  // grandfathered customer still sees a clean affordance.
  | 'starter' | 'professional' | 'business' | 'enterprise';


interface TierPillProps {
  tier: TierSlug | null | undefined;
  /** Override the rendered label (defaults to the tier display name). */
  label?: string;
  /** Make the pill clickable. When true, navigates to /client/billing. */
  clickable?: boolean;
  className?: string;
}


const TIER_STYLE: Record<TierSlug, { label: string; bg: string; fg: string }> = {
  free:         { label: 'Free',    bg: '#f0f2f7', fg: '#545454' },
  workspace:    { label: 'Team', bg: 'rgba(64,128,242,0.12)', fg: '#2554b0' },
  solo:         { label: 'Solo',    bg: 'rgba(64,128,242,0.12)',  fg: '#2554b0' },
  team:         { label: 'Legacy Team',    bg: 'rgba(255,234,0,0.22)',   fg: '#7a6500' },
  scale:        { label: 'Scale',   bg: 'rgba(99,102,241,0.14)',  fg: '#3b3897' },
  custom:       { label: 'Custom',  bg: 'rgba(220,38,127,0.12)',  fg: '#931957' },
  // Legacy chatbot-era tiers
  starter:      { label: 'Legacy',  bg: '#f0f2f7', fg: '#545454' },
  professional: { label: 'Legacy',  bg: '#f0f2f7', fg: '#545454' },
  business:     { label: 'Legacy',  bg: '#f0f2f7', fg: '#545454' },
  enterprise:   { label: 'Legacy',  bg: '#f0f2f7', fg: '#545454' },
};


export function TierPill({
  tier,
  label,
  clickable = false,
  className = '',
}: TierPillProps) {
  const navigate = useNavigate();
  const slug: TierSlug = (tier as TierSlug) || 'free';
  const style = TIER_STYLE[slug] || TIER_STYLE.free;
  const displayLabel = label ?? style.label;

  const baseStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 8px',
    borderRadius: 9999,
    background: style.bg,
    color: style.fg,
    fontSize: 11,
    fontWeight: 600,
    fontFamily: "var(--font-sans, 'IBM Plex Sans', system-ui, sans-serif)",
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    lineHeight: 1.3,
    border: 'none',
    cursor: clickable ? 'pointer' : 'default',
    transition: 'transform 0.15s ease',
  };

  if (clickable) {
    return (
      <button
        type="button"
        className={className}
        style={baseStyle}
        onClick={() => navigate('/client/billing')}
        aria-label={`Tier: ${displayLabel}. Manage billing.`}
      >
        <span>{displayLabel}</span>
        <ChevronRight size={10} strokeWidth={2.5} />
      </button>
    );
  }

  return (
    <span className={className} style={baseStyle}>
      {displayLabel}
    </span>
  );
}

export default TierPill;
